import React, { createContext, useEffect } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

// Create context for reCAPTCHA v3
const RecaptchaContext = createContext();

// Inner component that has access to reCAPTCHA
const RecaptchaLoader = ({ children }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    if (!executeRecaptcha) {
      console.log('reCAPTCHA v3 not ready yet, will retry...');
      return;
    }

    // Execute reCAPTCHA v3 on page load and push score to GTM dataLayer
    const handlePageLoad = async () => {
      try {
        const token = await executeRecaptcha('page_view');

        // In a real implementation, you would verify this token on your backend
        // For now, we'll extract the score (Note: actual score verification requires backend)
        // We'll use a placeholder score for GTM integration

        // Push to GTM dataLayer for analytics filtering
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'recaptcha_score',
          recaptchaToken: token,
          recaptchaAction: 'page_view'
        });

        console.log('reCAPTCHA v3 executed on page load successfully');
      } catch (error) {
        console.warn('reCAPTCHA v3 execution failed (non-critical):', error);
        // Don't throw - let the app continue without reCAPTCHA
      }
    };

    // Add a small delay to ensure reCAPTCHA script is fully loaded
    const timer = setTimeout(() => {
      handlePageLoad().catch(err => {
        console.warn('reCAPTCHA page load failed (non-critical):', err);
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [executeRecaptcha]);

  return children;
};

// Provider component that wraps the app
export const RecaptchaProvider = ({ children }) => {
  const siteKey = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY;

  // Disable reCAPTCHA on localhost (not in allowed domains)
  const isLocalhost = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1' ||
                      window.location.hostname.includes('192.168.');

  if (isLocalhost) {
    console.warn('⚠️ Running on localhost - reCAPTCHA is DISABLED for local development');
    console.warn('✅ Bot detection will work normally in production (capstone.net)');
    console.warn('✅ Forms will work normally without reCAPTCHA on localhost');
    return children;
  }

  if (!siteKey) {
    console.warn('reCAPTCHA v3 site key not found in environment variables');
    console.warn('Bot detection will be disabled. Forms will still work normally.');
    return children;
  }

  // Validate the site key format
  if (!siteKey.startsWith('6L')) {
    console.error('Invalid reCAPTCHA v3 site key format');
    console.warn('Bot detection will be disabled. Forms will still work normally.');
    return children;
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={siteKey}
      scriptProps={{
        async: true,
        defer: true,
        appendTo: 'head'
      }}
      container={{
        parameters: {
          badge: 'bottomright'
        }
      }}
    >
      <RecaptchaLoader>
        {children}
      </RecaptchaLoader>
    </GoogleReCaptchaProvider>
  );
};

// Custom hook to access reCAPTCHA v3 execution
export const useRecaptchaV3 = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const executeV3 = async (action = 'submit') => {
    if (!executeRecaptcha) {
      console.warn('reCAPTCHA v3 not available, continuing without it');
      return Promise.resolve(null); // Return resolved promise instead of just null
    }

    try {
      const token = await executeRecaptcha(action);
      return token;
    } catch (error) {
      console.warn('reCAPTCHA v3 execution failed (non-critical):', error);
      return Promise.resolve(null); // Return resolved promise on error
    }
  };

  return { executeV3 };
};

export default RecaptchaContext;
